/**
 * Auth Selectors
 * Memoized selectors for auth state
 */

import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "@/store/types";

// Base selectors
export const userDetails = (state: RootState) => state.auth;

// Redux-persist rehydration selector
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const selectIsRehydrated = (state: RootState) => (state as any)._persist?.rehydrated ?? true;

// Memoized selectors
export const selectIsAuthenticated = createSelector(
  userDetails,
  (auth) => auth.isAuthenticated
);

export const selectAccessToken = createSelector(
  userDetails,
  (auth) => auth.accessToken
);

export const selectLoginResponse = createSelector(
  userDetails,
  (auth) => auth.loginResponse
);

export const selectUserDetails = createSelector(
  selectLoginResponse,
  (loginResponse) => loginResponse?.loginUserDetails || null
);

export const selectUserRole = createSelector(
  selectUserDetails,
  (userDetails) => userDetails?.role || "user"
);

export const selectEmailRefId = createSelector(
  userDetails,
  (auth) => auth.emailRefId
);

export const selectPhoneRefId = createSelector(
  userDetails,
  (auth) => auth.phoneRefId
);

export const selectResetEmail = createSelector(
  userDetails,
  (auth) => auth.resetEmail
);

export const selectCIN = createSelector(
  selectUserDetails,
  (userDetails) => userDetails?.ciNo || null
);

// ============================================
// Pending Auth Selectors (New Login Flow)
// Must be defined before selectUserEmail which depends on them
// ============================================

export const selectPendingAuth = createSelector(
  userDetails,
  (auth) => auth.pendingAuth
);

export const selectPendingAuthEmail = createSelector(
  selectPendingAuth,
  (pendingAuth) => pendingAuth?.email || null
);

export const selectPendingAuthRefreshToken = createSelector(
  selectPendingAuth,
  (pendingAuth) => pendingAuth?.refreshToken || null
);

export const selectHasPendingAuth = createSelector(
  selectPendingAuth,
  (pendingAuth) => pendingAuth !== null
);

// User email selector - checks both loginUserDetails and pendingAuth
export const selectUserEmail = createSelector(
  [selectUserDetails, selectPendingAuth],
  (userDetails, pendingAuth) => userDetails?.emailId || pendingAuth?.email || null
);

export const selectUserName = createSelector(
  selectUserDetails,
  (userDetails) =>
    userDetails
      ? `${userDetails.firstName} ${userDetails.lastName}`.trim()
      : null
);

export const selectUserId = createSelector(
  selectUserDetails,
  (userDetails) => userDetails?.id || null
);

// ============================================
// WBA (WhatsApp Business Account) Selectors
// ============================================

export const selectWBADetails = createSelector(
  userDetails,
  (auth) => auth.wbaDetails
);

export const selectWBAPhoneNumbers = createSelector(
  selectWBADetails,
  (wbaDetails) => wbaDetails?.wba_phone_numbers || []
);

export const selectSelectedWBAPhoneNumber = createSelector(
  userDetails,
  (auth) => auth.selectedWBAPhoneNumber
);

export const selectSelectedWBAPhoneNoId = createSelector(
  selectSelectedWBAPhoneNumber,
  (phoneNumber) => phoneNumber?.wba_phone_no_id || null
);

export const selectSelectedWBAPhoneNo = createSelector(
  selectSelectedWBAPhoneNumber,
  (phoneNumber) => phoneNumber?.wba_phone_no || null
);

export const selectWBAId = createSelector(
  selectWBADetails,
  (wbaDetails) => wbaDetails?.wba_id || null
);

export const selectWBAAppId = createSelector(
  selectWBADetails,
  (wbaDetails) => wbaDetails?.wbaAppId || null
);

export const selectHasMultipleWBAPhones = createSelector(
  selectWBAPhoneNumbers,
  (phoneNumbers) => phoneNumbers.length > 1
);

// ============================================
// Organization Selectors
// ============================================

export const selectOrganizations = createSelector(
  userDetails,
  (auth) => auth.organizations ?? []
);

export const selectOrganizationsCount = createSelector(
  selectOrganizations,
  (organizations) => organizations?.length ?? 0
);

export const selectSelectedOrganization = createSelector(
  userDetails,
  (auth) => auth.selectedOrganization
);

export const selectSelectedOrganizationCIN = createSelector(
  selectSelectedOrganization,
  (organization) => organization?.cin || null
);

export const selectSelectedOrganizationName = createSelector(
  selectSelectedOrganization,
  (organization) => organization?.businessName || null
);

export const selectSelectedOrganizationAgentStatus = createSelector(
  selectSelectedOrganization,
  (organization) => organization?.agentStatus || null
);

export const selectIsAgentActive = createSelector(
  selectSelectedOrganizationAgentStatus,
  (agentStatus) => agentStatus === "ACTIVE"
);

export const selectIsAgentInactive = createSelector(
  selectSelectedOrganizationAgentStatus,
  (agentStatus) => agentStatus === "INACTIVE"
);

export const selectIsAgentDeleted = createSelector(
  selectSelectedOrganizationAgentStatus,
  (agentStatus) => agentStatus === "DELETED"
);

/**
 * Returns true if the agent is restricted (INACTIVE or DELETED).
 * Used by route guards to prevent access to protected pages.
 */
export const selectIsAgentRestricted = createSelector(
  selectSelectedOrganizationAgentStatus,
  (agentStatus) => agentStatus === "INACTIVE" || agentStatus === "DELETED"
);

export const selectOrganizationsLoading = createSelector(
  userDetails,
  (auth) => auth.organizationsLoading
);

export const selectOrganizationsError = createSelector(
  userDetails,
  (auth) => auth.organizationsError
);

export const selectHasMultipleOrganizations = createSelector(
  selectOrganizations,
  (organizations) => (organizations?.length ?? 0) > 1
);

export const selectRequiresOrganizationSelection = createSelector(
  userDetails,
  (auth) => auth.requiresOrganizationSelection
);

// ============================================
// Project Selectors
// ============================================

export const selectProjects = createSelector(
  userDetails,
  (auth) => auth.projects ?? []
);

export const selectProjectsCount = createSelector(
  selectProjects,
  (projects) => projects?.length ?? 0
);

export const selectSelectedProject = createSelector(
  userDetails,
  (auth) => auth.selectedProject
);

export const selectSelectedProjectId = createSelector(
  selectSelectedProject,
  (project) => project?.projectId || null
);

export const selectSelectedProjectKey = createSelector(
  selectSelectedProject,
  (project) => project?.projectKey || null
);

export const selectSelectedProjectName = createSelector(
  selectSelectedProject,
  (project) => project?.projectName || null
);

export const selectProjectsLoading = createSelector(
  userDetails,
  (auth) => auth.projectsLoading
);

export const selectProjectsError = createSelector(
  userDetails,
  (auth) => auth.projectsError
);

export const selectHasMultipleProjects = createSelector(
  selectProjects,
  (projects) => (projects?.length ?? 0) > 1
);

export const selectRequiresProjectSelection = createSelector(
  userDetails,
  (auth) => auth.requiresProjectSelection
);

export const selectDefaultProject = createSelector(
  selectProjects,
  (projects) => projects?.find((p) => p.isDefault) || null
);

/**
 * Selector to check if user has no projects available
 * Returns true when:
 * - Projects have been loaded (not loading, no error)
 * - Projects array is empty
 * - Organization is selected (projects should have been fetched)
 */
export const selectHasNoProjectsAvailable = createSelector(
  [
    selectProjects,
    selectProjectsLoading,
    selectProjectsError,
    selectSelectedOrganization,
  ],
  (projects, isLoading, error, selectedOrg) => {
    // Only return true if we've finished loading projects for a selected org
    // and there are no projects available
    return (
      selectedOrg !== null &&
      !isLoading &&
      !error &&
      projects.length === 0
    );
  }
);

// ============================================
// Project Token Selectors
// ============================================

export const selectProjectAccessToken = createSelector(
  userDetails,
  (auth) => auth.projectAccessToken
);

export const selectProjectRefreshToken = createSelector(
  userDetails,
  (auth) => auth.projectRefreshToken
);

export const selectHasProjectTokens = createSelector(
  [selectProjectAccessToken, selectProjectRefreshToken],
  (accessToken, refreshToken) => accessToken !== null && refreshToken !== null
);

// ============================================
// Flow Control Selectors
// ============================================

export const selectIsSessionComplete = createSelector(
  [
    selectIsAuthenticated,
    selectSelectedOrganization,
    selectSelectedProject,
    selectRequiresOrganizationSelection,
    selectRequiresProjectSelection,
  ],
  (
    isAuthenticated,
    selectedOrg,
    selectedProj,
    requiresOrgSelection,
    requiresProjSelection
  ) =>
    isAuthenticated &&
    selectedOrg !== null &&
    selectedProj !== null &&
    !requiresOrgSelection &&
    !requiresProjSelection
);

export const selectSessionSetupStep = createSelector(
  [
    selectIsAuthenticated,
    selectHasPendingAuth,
    selectRequiresOrganizationSelection,
    selectRequiresProjectSelection,
    selectSelectedOrganization,
    selectSelectedProject,
  ],
  (
    isAuthenticated,
    hasPendingAuth,
    requiresOrgSelection,
    requiresProjSelection,
    selectedOrg,
    selectedProj
  ): "unauthenticated" | "select-organization" | "select-project" | "complete" => {
    // If we have pending auth but not yet authenticated, we're in org selection
    if (hasPendingAuth && !isAuthenticated) return "select-organization";
    if (!isAuthenticated && !hasPendingAuth) return "unauthenticated";
    if (requiresOrgSelection || !selectedOrg) return "select-organization";
    if (requiresProjSelection || !selectedProj) return "select-project";
    return "complete";
  }
);

// Derived CIN selector - prefers selected organization CIN over login response CIN
export const selectActiveCIN = createSelector(
  [selectSelectedOrganizationCIN, selectCIN],
  (selectedOrgCIN, loginCIN) => selectedOrgCIN || loginCIN
);

// ============================================
// Auth Transition Loader Selectors (Optimized Login Flow)
// ============================================

export const selectAuthTransitionLoading = createSelector(
  userDetails,
  (auth) => auth.authTransitionLoading
);

export const selectAuthTransitionMessage = createSelector(
  userDetails,
  (auth) => auth.authTransitionMessage
);

